import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { vehiclesService } from "../services";
import type { VehicleListParams, VehicleOdometerHistoryParams } from "../types";

export const VEHICLE_ODOMETER_SUMMARY_QUERY_KEY = "vehicle-odometer-summary";
export const VEHICLE_ODOMETER_HISTORY_QUERY_KEY = "vehicle-odometer-history";
export const VEHICLE_ODOMETER_LATEST_CHECKLIST_QUERY_KEY =
	"vehicle-odometer-latest-checklist";
export const VEHICLE_ODOMETER_DETAIL_QUERY_KEY = "vehicle-odometer-detail";

export const useGetVehicles = (params: VehicleListParams) =>
	useQuery({
		queryKey: ["vehicles", params],
		queryFn: () => vehiclesService.getVehicles(params),
	});

export const useGetVehicleById = (id: string) =>
	useQuery({
		queryKey: ["vehicle", id],
		queryFn: () => vehiclesService.getVehicleById(id),
		enabled: !!id,
	});

export const useGetAllVehicles = (enabled = true) =>
	useQuery({
		queryKey: ["vehicles-all"],
		queryFn: () => vehiclesService.getAllVehicles(),
		enabled,
	});

export const useGetVehicleOdometerSummary = (vehicleId: string) =>
	useQuery({
		queryKey: [VEHICLE_ODOMETER_SUMMARY_QUERY_KEY, vehicleId],
		queryFn: () => vehiclesService.getVehicleOdometerSummary(vehicleId),
		enabled: !!vehicleId,
	});

export const useGetVehicleOdometerHistory = (
	vehicleId: string,
	params: VehicleOdometerHistoryParams
) =>
	useQuery({
		queryKey: [VEHICLE_ODOMETER_HISTORY_QUERY_KEY, vehicleId, params],
		queryFn: () => vehiclesService.getVehicleOdometerHistory(vehicleId, params),
		enabled: !!vehicleId,
		placeholderData: (previousData, previousQuery) =>
			previousQuery?.queryKey[1] === vehicleId
				? keepPreviousData(previousData)
				: undefined,
	});

export const useGetVehicleOdometerLatestChecklist = (
	vehicleId: string,
	enabled = true
) =>
	useQuery({
		queryKey: [VEHICLE_ODOMETER_LATEST_CHECKLIST_QUERY_KEY, vehicleId],
		queryFn: () => vehiclesService.getVehicleOdometerLatestChecklist(vehicleId),
		enabled: !!vehicleId && enabled,
	});

export const useGetVehicleOdometerDetail = (
	vehicleId: string,
	vehicleOdometerId: string,
	enabled = true
) =>
	useQuery({
		queryKey: [VEHICLE_ODOMETER_DETAIL_QUERY_KEY, vehicleId, vehicleOdometerId],
		queryFn: () =>
			vehiclesService.getVehicleOdometerDetail(vehicleId, vehicleOdometerId),
		enabled: !!vehicleId && !!vehicleOdometerId && enabled,
	});
